import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import * as turf from '@turf/turf';
import { DateTime } from 'luxon';
import { Marker } from 'react-map-gl';

import { BambiMapView } from 'src/common/BambiMapView/BambiMapView';
import { LocationLogEntryTripMinimalReadOnlyRead } from 'src/common/external/bambi-api/bambiApi';

export type LocationWithColor = LocationLogEntryTripMinimalReadOnlyRead & {
  color: string;
};

export function EventDeviceLogMapView({
  locations,
}: {
  locations: LocationWithColor[];
}) {
  const locationsWithBearing = locations.map((location, i) => {
    const nextPoint = locations[i + 1];
    if (!nextPoint) {
      return {
        ...location,
        bearing: null,
        midpoint: null,
        speed: null,
        distance: null,
      };
    }

    const point1 = turf.point([
      location.location_log_entry.longitude,
      location.location_log_entry.latitude,
    ]);
    const point2 = turf.point([
      nextPoint.location_log_entry.longitude,
      nextPoint.location_log_entry.latitude,
    ]);

    const bearing = turf.bearing(point1, point2);
    const distance = turf.distance(point1, point2, { units: 'miles' });
    const speed =
      distance /
      DateTime.fromISO(nextPoint.location_log_entry.device_timestamp)
        .diff(DateTime.fromISO(location.location_log_entry.device_timestamp))
        .as('hours');
    const midpoint = turf.midpoint(point1, point2).geometry.coordinates;
    return { ...location, bearing, distance, speed, midpoint };
  });

  return (
    <BambiMapView mapViewProps={{ initialViewState: { zoom: 11 } }}>
      {locationsWithBearing.map((location, i) => {
        const shouldRenderArrowMarker = location.bearing && i > 0;
        return (
          <>
            <Marker
              key={`${location.location_log_entry.latitude}-${location.location_log_entry.longitude}-${location.color}`}
              latitude={location.location_log_entry.latitude}
              longitude={location.location_log_entry.longitude}
              color={location.color}
            >
              {shouldRenderArrowMarker ? (
                <ArrowUpCircleIcon
                  className="z-50 h-6 w-6"
                  strokeWidth={2}
                  fill={location.color}
                  style={{ transform: `rotate(${location.bearing}deg)` }}
                />
              ) : null}
            </Marker>
            {location.midpoint ? (
              <Marker
                latitude={location.midpoint[1]}
                longitude={location.midpoint[0]}
              >
                <div className="h-2 w-2 text-xs">
                  {location.speed ? `${location.speed.toFixed(2)} mph` : null}
                </div>
              </Marker>
            ) : null}
          </>
        );
      })}
    </BambiMapView>
  );
}
